import React, { useEffect, useState, useRef } from 'react';
import axios from '../axiosInstance';
import { motion, AnimatePresence } from 'framer-motion';
import API_ENDPOINTS from '../api/apiEndpoints';
import { FiX } from 'react-icons/fi';

const InventoryUpload = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Commodity filter states
  const [commodities, setCommodities] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState('');
  const [commoditySearch, setCommoditySearch] = useState('');
  const [commodityDropdownOpen, setCommodityDropdownOpen] = useState(false);

  // Scheme filter states
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [schemeSearch, setSchemeSearch] = useState('');
  const [schemeDropdownOpen, setSchemeDropdownOpen] = useState(false);
  // state filter
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  //Season filter
  const [season, setSeason] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [seasonSearch, setSeasonSearch] = useState('');
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);

  //warehouse filter
  const [warehouse, setWarehouse] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [warehouseDropdownOpen, setWarehouseDropdownOpen] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // User role, state, and branch for branch users
  const [role, setRole] = useState('');
  const [userStateId, setUserStateId] = useState('');
  const [userBranchId, setUserBranchId] = useState('');

  // Form state for adding single inventory
  const [inventoryForm, setInventoryForm] = useState({
    warehouse_code: '',
    state: '',
    whr_id: '',
    whr_date: '',
    bags: '',
    quantity: '',
    commodity: '',
    scheme: '',
    season: '',
    file: null,
  });

  // Filtered warehouses based on selected state
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [formError, setFormError] = useState('');

  // Fetch paginated inventory
  const fetchData = async (
    pageNo,
    searchWhr = '',
    commodityId = '',
    schemeId = '',
    stateId = '',
    seasonId = '',
    warehouseId = ''
  ) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      let url = `inventory/list?page=${pageNo}`;
      if (searchWhr) url += `&whr_id=${searchWhr}`;
      if (commodityId) url += `&commodity_id=${commodityId}`;
      if (schemeId) url += `&scheme_id=${schemeId}`;
      if (stateId) url += `&state_id=${stateId}`;
      if (seasonId) url += `&season_id=${seasonId}`;
      if (warehouseId) url += `&warehouse_id=${warehouseId}`;

      const res = await axios.get(url);

      setData(res.data.data);
      setTotal(res.data.total);
      setLimit(res.data.limit);
    } catch (err) {
      console.error('Error fetching inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch commodities
  const fetchCommodities = async () => {
    try {
      // console.log('Fetching commodities...'); // Debug log
      const token = localStorage.getItem('token');
      const res = await axios.get('commodity');
      // console.log('Commodities fetched:', res.data);
      setCommodities(res.data || []);
    } catch (err) {
      console.error('Error fetching commodities:', err);
    }
  };

  // Fetch schemes
  const fetchSchemes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('schemes');
      // console.log(res.data);
      setSchemes(res.data || []);
    } catch (err) {
      console.error('Error fetching schemes:', err);
    }
  };

  const fetchStates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('states', {
        headers: { Authorization: `Bearer ${token}` },
      });
      // console.log(res.data);
      setStates(res.data || []);
    } catch (err) {
      console.error('Error fetching schemes:', err);
    }
  };

  const fetchSeason = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('seasons?paginate=false');
      // console.log(res.data);
      setSeason(res.data || []);
    } catch (err) {
      console.error('Error fetching Season:', err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`warehouse?paginate=false`);
      // console.log(res.data);
      setWarehouse(res.data || []);
    } catch (err) {
      console.error('Error fetching Season:', err);
    }
  };

  // Use ref to prevent duplicate API calls
  const initialDataFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch data if it hasn't been fetched already
    if (!initialDataFetchedRef.current) {
      initialDataFetchedRef.current = true;
      fetchCommodities();
      fetchSchemes();
      fetchStates();
      fetchSeason();
      fetchWarehouses();

      // Get user role, state, and branch from localStorage
      const user = JSON.parse(localStorage.getItem('user'));
      const userRole = user?.roles?.[0]?.name || '';
      const stateId = user?.state?.id || '';
      const stateName = user?.state?.name || '';
      const branchId = user?.branch?.id || '';
      const branchName = user?.branch?.name || '';
      setRole(userRole);
      setUserStateId(stateId);
      setUserBranchId(branchId);

      // Auto-set state filter for Branch users
      if (userRole === 'Branch' && stateId) {
        setSelectedState(stateId);
        setStateSearch(stateName);
      }
    }
  }, []);

  // Fetch inventory when filters/search/page change
  const fetchDataRef = useRef(false);

  useEffect(() => {
    // Use a small timeout to prevent duplicate calls
    const timeoutId = setTimeout(() => {
      fetchDataRef.current = true;
      fetchData(
        page,
        search,
        selectedCommodity,
        selectedScheme,
        selectedState,
        selectedSeason,
        selectedWarehouse
      );
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    page,
    search,
    selectedCommodity,
    selectedScheme,
    selectedState,
    selectedSeason,
    selectedWarehouse,
  ]);

  // Handle close upload modal
  const handleCloseUploadModal = () => {
    setShowModal(false);
    setSelectedFile(null);
    setUploadError('');
  };

  // Handle close add inventory modal
  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setFormError('');
    setInventoryForm({
      warehouse_code: '',
      state: '',
      whr_id: '',
      whr_date: '',
      bags: '',
      quantity: '',
      commodity: '',
      scheme: '',
      season: '',
      file: null,
    });
  };

  // Add single inventory
  const handleAddInventory = async () => {
    // Form validation
    if (!inventoryForm.warehouse_code) {
      setFormError('Please select a warehouse');
      return;
    }
    if (!inventoryForm.state) {
      setFormError('Please select a state');
      return;
    }
    if (!inventoryForm.whr_id) {
      setFormError('WHR ID is required');
      return;
    }
    if (!inventoryForm.whr_date) {
      setFormError('WHR Date is required');
      return;
    }
    if (!inventoryForm.bags || inventoryForm.bags <= 0) {
      setFormError('Please enter a valid number of bags');
      return;
    }
    if (!inventoryForm.quantity || inventoryForm.quantity <= 0) {
      setFormError('Please enter a valid quantity');
      return;
    }
    if (!inventoryForm.commodity) {
      setFormError('Please select a commodity');
      return;
    }
    if (!inventoryForm.scheme) {
      setFormError('Please select a scheme');
      return;
    }
    if (!inventoryForm.season) {
      setFormError('Please select a season');
      return;
    }
    if (!inventoryForm.file) {
      setFormError('Please select a PDF file.');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Create FormData payload matching the multipart request format
      const formData = new FormData();
      formData.append('warehouse_code', inventoryForm.warehouse_code);
      formData.append('state', inventoryForm.state);
      formData.append('whr_id', inventoryForm.whr_id);
      formData.append('whr_date', inventoryForm.whr_date);
      formData.append('bags', inventoryForm.bags);
      formData.append('quantity', inventoryForm.quantity);
      formData.append('commodity', inventoryForm.commodity);
      formData.append('scheme', inventoryForm.scheme);
      formData.append('season', inventoryForm.season);
      formData.append('file', inventoryForm.file);

      const res = await axios.post(API_ENDPOINTS.inventory.store, formData);

      if (res.data) {
        alert('✅ Inventory added successfully');
        setShowAddModal(false);
        setFormError('');
        setInventoryForm({
          warehouse_code: '',
          state: '',
          whr_id: '',
          whr_date: '',
          bags: '',
          quantity: '',
          commodity: '',
          scheme: '',
          season: '',
          file: null,
        });
        // Refresh inventory data
        fetchData(
          page,
          search,
          selectedCommodity,
          selectedScheme,
          selectedState,
          selectedSeason,
          selectedWarehouse
        );
      }
    } catch (err) {
      console.error(
        'Error adding inventory:',
        err.response?.data || err.message
      );
      setFormError(
        err.response?.data?.message ||
        'Failed to add inventory. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Upload Excel
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('⚠️ Please select a file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const res = await axios.post(
        API_ENDPOINTS.inventory.uploadExcel,
        formData,
      );

      if (res.data && res.data.success) {
        alert('✅ File uploaded successfully');
        fetchData(
          page,
          search,
          selectedCommodity,
          selectedScheme,
          selectedState,
          selectedSeason,
          selectedWarehouse
        );
        handleCloseUploadModal();
      } else {
        setUploadError(
          res.data?.message || '⚠️ Upload failed, please try again.'
        );
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(
        err.response?.data?.message || '⚠️ Upload failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Download Sample
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('download/inventory-upload-sample', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory-sample.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // filter commodities with search text
  // Commodity filter
  const filteredCommodities = commodities.filter((c) =>
    (c?.commodityname || '')
      .toLowerCase()
      .includes(commoditySearch.toLowerCase())
  );

  // Scheme filter
  const filteredSchemes = schemes.filter((s) =>
    (s?.name || '').toLowerCase().includes(schemeSearch.toLowerCase())
  );

  // State filter
  const filteredState = states.filter((s) =>
    (s?.name || '').toLowerCase().includes(stateSearch.toLowerCase())
  );

  // Season filter
  const filteredSeason = season.filter((s) =>
    (s?.name || '').toLowerCase().includes(seasonSearch.toLowerCase())
  );

  // Warehouse filter - also filter by selected state
  const filteredWarehouse = warehouse.filter((s) => {
    const matchesSearch = (s?.warehouse_name || '')
      .toLowerCase()
      .includes(warehouseSearch.toLowerCase());
    const matchesState = selectedState ? s?.state?.id === selectedState : true;
    return matchesSearch && matchesState;
  });

  const closeAllDropdowns = () => {
    setSeasonDropdownOpen(false);
    setSchemeDropdownOpen(false);
    setStateDropdownOpen(false);
    setCommodityDropdownOpen(false);
    setWarehouseDropdownOpen(false);
  };

  // clear filters
  const clearFilters = () => {
    closeAllDropdowns();
    setSearch('');
    setSelectedCommodity('');
    setSelectedState('');
    setSelectedSeason('');
    setSelectedWarehouse('');
    setSelectedScheme('');
    setCommoditySearch('');
    setSchemeSearch('');
    setStateSearch('');
    setSeasonSearch('');
    setWarehouseSearch('');
    setPage(1);
    fetchData(1, '', '', '', '', '', '');
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query parameters based on current filters
      let queryParams = new URLSearchParams();
      if (search) queryParams.append('whr_id', search);
      if (selectedCommodity)
        queryParams.append('commodity_id', selectedCommodity);
      if (selectedScheme) queryParams.append('scheme_id', selectedScheme);
      if (selectedState) queryParams.append('state_id', selectedState);
      if (selectedSeason) queryParams.append('season_id', selectedSeason);
      if (selectedWarehouse)
        queryParams.append('warehouse_id', selectedWarehouse);

      // Make API call to export endpoint
      const res = await axios.get(
        `inventory/export-whrwise-excel?${queryParams.toString()}`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `inventory-export-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert('✅ Export successful');
    } catch (err) {
      console.error('Export failed:', err);
      alert('⚠️ Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-blue-100 p-10 text-gray-900">
      <div className="flex flex-wrap items-center justify-between mb-8">
        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-extrabold text-blue-800 drop-shadow-sm"
        >
          📦 WHR Wise Stock List
        </motion.h1>
        <div className="flex flex-wrap gap-3">
          {role !== 'User' && (
            <>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-xl shadow-md hover:bg-blue-700"
              >
                ➕ Add Inventory
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="bg-green-600 text-white px-6 py-2 rounded-xl shadow-md hover:bg-green-700"
              >
                ⬆️ Upload Excel
              </button>
            </>
          )}
          <button
            onClick={handleExport}
            disabled={loading}
            className="bg-purple-600 text-white px-6 py-2 rounded-xl shadow-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Exporting...' : '📥 Export Excel'}
          </button>
        </div>
      </div>

      {/* 🔍 Filters Row */}
      <div className="flex flex-wrap gap-3 mb-3">
        {/* WHR Search */}
        <input
          type="text"
          placeholder="Search by WHR ID..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border text-base shadow-sm focus:ring-2 focus:ring-blue-400"
        />

        {/* Season Dropdown */}
        <div className="relative flex-1 min-w-[200px]">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setSchemeDropdownOpen(false);
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setSeasonDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
          >
            <span>
              {selectedSeason
                ? seasonSearch || 'Season Selected'
                : 'Select Season'}
            </span>
            <div className="flex items-center gap-2">
              {selectedSeason && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSeason('');
                    setSeasonSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {seasonDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {seasonDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Season..."
                value={seasonSearch}
                onChange={(e) => setSeasonSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSeason.length === 0 && (
                <p className="p-2 text-gray-500">No Season found</p>
              )}
              {filteredSeason.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedSeason(c.id);
                    setSeasonSearch(c.name);
                    setSeasonDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedSeason === c.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {c.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Scheme Dropdown */}
        <div className="relative flex-1 min-w-[200px]">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setSeasonDropdownOpen(false);
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setSchemeDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
          >
            <span>
              {selectedScheme
                ? schemeSearch || 'Scheme Selected'
                : 'Select Scheme'}
            </span>
            <div className="flex items-center gap-2">
              {selectedScheme && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedScheme('');
                    setSchemeSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {schemeDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {schemeDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Scheme..."
                value={schemeSearch}
                onChange={(e) => setSchemeSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredSchemes.length === 0 && (
                <p className="p-2 text-gray-500">No schemes found</p>
              )}
              {filteredSchemes.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedScheme(s.id);
                    setSchemeSearch(s.name);
                    setSchemeDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedScheme === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* State Dropdown */}
        <div className="relative flex-1 min-w-[200px]">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setSeasonDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setStateDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
          >
            <span>
              {selectedState ? stateSearch || 'State Selected' : 'Select State'}
            </span>
            <div className="flex items-center gap-2">
              {selectedState && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedState('');
                    setStateSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {stateDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {stateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search State..."
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredState.length === 0 && (
                <p className="p-2 text-gray-500">No state found</p>
              )}
              {filteredState.map((s) => (
                <div
                  key={s.id}
                  onClick={() => {
                    setSelectedState(s.id);
                    setStateSearch(s.name);
                    setStateDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedState === s.id ? 'bg-blue-200 font-semibold' : ''
                    }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Commodity Dropdown */}
        <div className="relative flex-1 min-w-[200px]">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setSeasonDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setStateDropdownOpen(false);
              setWarehouseDropdownOpen(false);
              setCommodityDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
          >
            <span>
              {selectedCommodity
                ? commoditySearch || 'Commodity Selected'
                : 'Select Commodity'}
            </span>

            <div className="flex items-center gap-2">
              {selectedCommodity && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCommodity('');
                    setCommoditySearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {commodityDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {commodityDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Commodity..."
                value={commoditySearch}
                onChange={(e) => setCommoditySearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredCommodities.length === 0 && (
                <p className="p-2 text-gray-500">No commodities found</p>
              )}
              {filteredCommodities.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedCommodity(c.id);
                    setCommoditySearch(c.commodityname);
                    setCommodityDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedCommodity === c.id
                    ? 'bg-blue-200 font-semibold'
                    : ''
                    }`}
                >
                  {c.commodityname}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warehouse Dropdown */}
        <div className="relative flex-1 min-w-[200px]">
          <div
            onClick={() => {
              // Close all other dropdowns first
              setSeasonDropdownOpen(false);
              setSchemeDropdownOpen(false);
              setStateDropdownOpen(false);
              setCommodityDropdownOpen(false);
              setWarehouseDropdownOpen((prev) => !prev);
            }}
            className="px-4 py-2 border rounded-xl bg-white shadow-sm cursor-pointer text-base flex justify-between items-center"
          >
            <span>
              {selectedWarehouse
                ? warehouseSearch || 'Warehouse Selected'
                : 'Select Warehouse'}
            </span>
            <div className="flex items-center gap-2">
              {selectedWarehouse && (
                <FiX
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedWarehouse('');
                    setWarehouseSearch('');
                    setPage(1);
                  }}
                  className="text-gray-400 hover:text-red-500 text-lg cursor-pointer"
                  title="Clear"
                />
              )}
              <span className="text-gray-500">
                {warehouseDropdownOpen ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {warehouseDropdownOpen && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto mt-1 z-10">
              <input
                type="text"
                placeholder="Search Warehouse..."
                value={warehouseSearch}
                onChange={(e) => setWarehouseSearch(e.target.value)}
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              {filteredWarehouse.length === 0 && (
                <p className="p-2 text-gray-500">No Warehouse found</p>
              )}
              {filteredWarehouse.map((c) => (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedWarehouse(c.id);
                    setWarehouseSearch(c.warehouse_name);
                    setWarehouseDropdownOpen(false);
                    setPage(1);
                  }}
                  className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedWarehouse === c.id
                    ? 'bg-blue-200 font-semibold'
                    : ''
                    }`}
                >
                  {c.warehouse_name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 🔘 Action Buttons Row */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={clearFilters}
          className="bg-red-500 text-white px-5 py-2 rounded-xl shadow-md hover:bg-red-600"
        >
          ❌ Clear Filters
        </button>
      </div>

      {/* Data Table */}
      {data && data.length > 0 ? (
        <motion.div className="overflow-hidden rounded-2xl shadow-xl border border-gray-100 bg-white">
          <div className="overflow-x-auto">
            {loading ? (
              <p className="text-center text-xl font-semibold text-gray-500 py-10">
                Loading...
              </p>
            ) : (
              <table className="w-full border-collapse text-left">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    {[
                      'State',
                      'Commodity',
                      'Scheme',
                      'Season',
                      'Warehouse',
                      'WHR ID',
                      'WHR Date',
                      'Qty (MT)',
                      'Bags',
                      'Age Of Stock (Days)',
                    ].map((header) => (
                      <th
                        key={header}
                        className="py-4 px-6 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {data.map((item) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="py-4 px-6 text-sm text-gray-700 font-medium">{item.warehouse?.state.name}</td>
                      <td className="py-4 px-6 text-sm text-blue-700 font-medium">{item.commodity?.commodityname}</td>
                      <td className="py-4 px-6 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {item.scheme?.name}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700">{item.season?.name}</td>
                      <td className="py-4 px-6 text-sm text-gray-700">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {item.warehouse?.warehouse_name}
                          </span>
                          <span className="text-xs text-blue-500 mt-1">
                            {item.warehouse?.warehouse_code}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm">
                        <div className="flex flex-col gap-2 items-start">
                          <span className="font-bold text-gray-900">{item.whr_id}</span>
                          {item.whr_file ? (
                            <a
                              href={item.whr_file}
                              download
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium cursor-pointer transition-colors"
                            >
                              📥 Download
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">No file</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700">
                        {item.whr_date
                          ? new Date(item.whr_date).toLocaleDateString('en-GB')
                          : ''}
                      </td>
                      <td className="py-4 px-6 text-sm">
                        <div className="flex flex-col gap-1">
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-green-50 text-green-700 font-bold border border-green-100">{item.quantity}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{item.blocked_qty}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{item.current_qty}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{item.withdrawal_qty}</span></div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm">
                        <div className="flex flex-col gap-1">
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Total:</span> <span className="text-gray-700 font-medium">{item.bags}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Blocked:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-red-50 text-red-700 font-bold border border-red-100">{item.blocked_bags}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Current:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 font-bold border border-blue-100">{item.current_bags}</span></div>
                          <div className="whitespace-nowrap"><span className="text-gray-500 text-xs">Withdrawn:</span> <span className="inline-block px-2 py-0.5 rounded-lg bg-gray-50 text-gray-700 font-bold border border-gray-100">{item.withdrawal_bags}</span></div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-700">{item.age_of_stock}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="text-center text-gray-500 py-2 text-xl font-semibold">
          No inventory data found.
        </div>
      )}

      {/* Pagination */}
      {data && data.length > 0 ? (
        <div className="flex justify-between items-center mt-8">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className={`px-6 py-3 rounded-xl text-lg font-medium shadow-md transition-all ${page === 1
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            ⬅ Prev
          </button>
          <span className="text-xl font-semibold text-gray-700">
            Page {page} of {Math.ceil(total / limit)}
          </span>
          <button
            disabled={page >= Math.ceil(total / limit)}
            onClick={() => setPage((p) => p + 1)}
            className={`px-6 py-3 rounded-xl text-lg font-medium shadow-md transition-all ${page >= Math.ceil(total / limit)
              ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            Next ➡
          </button>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-2 text-xl font-semibold"></div>
      )}

      {/* Add Inventory Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-8 w-[90%] max-w-[600px] max-h-[90vh] overflow-y-auto "
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <h2 className="text-2xl font-bold mb-6 text-blue-700">
                ➕ Add New Inventory
              </h2>

              {formError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-6">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* State Dropdown */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    State
                  </label>
                  <select
                    value={inventoryForm.state}
                    onChange={(e) => {
                      // Update state value
                      setInventoryForm({
                        ...inventoryForm,
                        state: e.target.value,
                        warehouse_code: '',
                      });

                      // Filter warehouses based on selected state
                      if (e.target.value) {
                        const stateId = states.find(
                          (s) => s.iso2 === e.target.value
                        )?.id;
                        const warehousesInState = warehouse.filter(
                          (w) => w.state?.id === stateId
                        );
                        setFilteredWarehouses(warehousesInState);
                      } else {
                        setFilteredWarehouses([]);
                      }
                    }}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.iso2}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warehouse Dropdown */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Warehouse
                  </label>
                  <select
                    value={inventoryForm.warehouse_code}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        warehouse_code: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                    disabled={!inventoryForm.state}
                  >
                    <option value="">
                      {!inventoryForm.state
                        ? 'Select State First'
                        : 'Select Warehouse'}
                    </option>
                    {filteredWarehouses.map((w) => (
                      <option key={w.id} value={w.warehouse_code}>
                        {w.warehouse_name} ({w.warehouse_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* WHR ID */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    WHR ID
                  </label>
                  <input
                    type="text"
                    value={inventoryForm.whr_id}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        whr_id: e.target.value,
                      })
                    }
                    placeholder="WHR/1234"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                {/* WHR Date */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    WHR Date
                  </label>
                  <input
                    type="date"
                    value={inventoryForm.whr_date}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        whr_date: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Bags */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Bags
                  </label>
                  <input
                    type="number"
                    value={inventoryForm.bags}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        bags: e.target.value,
                      })
                    }
                    placeholder="88.88"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Quantity */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Quantity (MT)
                  </label>
                  <input
                    type="number"
                    value={inventoryForm.quantity}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        quantity: e.target.value,
                      })
                    }
                    placeholder="88.88"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Commodity */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Commodity
                  </label>
                  <select
                    value={inventoryForm.commodity}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        commodity: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Commodity</option>
                    {commodities.map((c) => (
                      <option key={c.id} value={c.commoditycode}>
                        {c.commodityname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scheme */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Scheme
                  </label>
                  <select
                    value={inventoryForm.scheme}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        scheme: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Scheme</option>
                    {schemes.map((s) => (
                      <option key={s.id} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Season */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Season
                  </label>
                  <select
                    value={inventoryForm.season}
                    onChange={(e) =>
                      setInventoryForm({
                        ...inventoryForm,
                        season: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  >
                    <option value="">Select Season</option>
                    {season.map((s) => (
                      <option key={s.id} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* WHR PDF Upload */}
                <div className="col-span-1">
                  <label className="block text-gray-700 mb-1 font-medium">
                    Upload WHR PDF <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  </label>
                  <div className="relative">
                    <input
                      id="whrPdfInput"
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (!f) return;
                        if (f.type !== 'application/pdf') {
                          setFormError('Only PDF files are allowed for WHR document.');
                          return;
                        }
                        if (f.size > 10 * 1024 * 1024) {
                          setFormError('PDF file size must be under 10 MB.');
                          return;
                        }
                        setFormError('');
                        setInventoryForm({ ...inventoryForm, file: f });
                      }}
                    />
                    <div
                      onClick={() => document.getElementById('whrPdfInput').click()}
                      className="w-full border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-sm flex justify-between items-center h-[42px] overflow-hidden"
                    >
                      <span className="truncate text-gray-500 max-w-[80%]">
                        {inventoryForm.file ? inventoryForm.file.name : 'Select PDF file'}
                      </span>
                      {inventoryForm.file ? (
                        <span
                          className="text-red-500 hover:text-red-700 font-bold text-xs cursor-pointer ml-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setInventoryForm({ ...inventoryForm, file: null });
                            document.getElementById('whrPdfInput').value = '';
                          }}
                        >
                          ✕
                        </span>
                      ) : (
                        <span className="text-gray-400">📎</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={handleAddInventory}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  ✅ Save
                </button>
                <button
                  onClick={handleCloseAddModal}
                  className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500"
                >
                  ❌ Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-8 w-96 "
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <h2 className="text-2xl font-bold mb-4 text-blue-700">
                📤 Upload Inventory Excel
              </h2>

              <input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="w-full mb-4 border rounded-lg px-4 py-2"
              />

              {uploadError && (
                <div className="text-red-500 mb-4">{uploadError}</div>
              )}

              <a
                href="#"
                onClick={handleDownload}
                className="text-blue-600 hover:underline block mb-3 text-center"
              >
                ⬇️ Download Sample
              </a>

              <div className="flex justify-between gap-3">
                <button
                  onClick={handleUpload}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  ✅ Submit
                </button>
                <button
                  onClick={handleCloseUploadModal}
                  className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500"
                >
                  ❌ Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default InventoryUpload;
